// pages/tasks/OwnTicketModal.jsx
//
// "Own Ticket" -- a Data-Analyst/Developer self-owned work item, entirely
// distinct from "Raise Complaint" (technician-routed). Modeled directly on
// the Technician's own "Add Task" modal
// (pages/technicians/TaskManagementPage.js) -- same fields, same backend
// endpoints, same category/subcategory ownership rules -- NOT the generic
// Project/Module "Create Work Ticket" form (TaskCreateForm.jsx's ticket
// mode), which is the wrong shape for this flow (see the Data Analyst
// Raise Complaint + Own Ticket enhancement's "Own Ticket" requirement).
//
// Reuse, not duplication:
//   - GET /categories/technician-owned and GET /subcategories/technician-
//     owned/:categoryId are the SAME ownership-scoped endpoints Technician's
//     Add Task already uses (backend-scoped to req.user.id via
//     category_staff -> sub_category_staff fallback, controllers/
//     categoryController.js#getTechnicianOwnedCategories / controllers/
//     subcategoryController.js#getTechnicianOwnedSubcategoriesByCategory --
//     both role-agnostic despite the "technician-owned" name, so reused
//     here verbatim for Data-Analyst/Developer ownership, never a second
//     parallel endpoint).
//   - POST /tasks is the SAME legacy endpoint Technician's Add Task posts
//     to (controllers/taskController.js#createTask) -- generalized (not
//     duplicated) to allow ANY caller to self-assign their own work
//     (services/categoryStaffService.js#isOwnedByUser re-validates
//     Category/Subcategory ownership server-side on that same self-assign
//     path, so this can never diverge from what the dropdowns above show).
//   - Same Task Date / conditional TAT model as everywhere else in the app
//     (services/subcategoryTatService.js via the backend, no client-side
//     due_at calculation here).
//
// Staff/User is ALWAYS the logged-in user, non-editable -- "Own Ticket"
// means the caller is creating this for themselves; there is no picker to
// impersonate anyone else with (unlike Technician's Add Task, which lets a
// Technician create a ticket "on behalf of" a different Staff/User).
//
// Phase 66 (Data Analyst only -- the Developer path is unchanged):
//   - Schools         optional searchable multi-select (zero, one or many),
//                     "+ Add New School"
//   - Subcategories * searchable multi-select of the chosen Category's
//                     subcategories; a Category change clears them
//   - TAT Level *     High 4 Hours / Medium 2 Days / Low 5 Days (fixed, only
//                     the level is sent -- the backend derives the value) or
//                     New Requirement with a custom Value + Unit
//   - Recent TAT Entries get an Edit action (EditOwnTicketModal, TAT only)
import React, { useContext, useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";
import MultiSelectChips from "../../components/MultiSelectChips";
import DataAnalystTatPicker from "./DataAnalystTatPicker";
import EditOwnTicketModal from "./EditOwnTicketModal";
import { formatTat, levelLabel, tatErrors, tatPayload } from "./dataAnalystTat";
import "./TicketModalTheme.css";

function todayDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

const initialForm = {
  title: "",
  description: "",
  unit_id: "",
  category_id: "",
  // Developer: one subcategory. Data Analyst: sub_category_ids (the first is
  // also sent as sub_category_id, the primary).
  sub_category_id: "",
  sub_category_ids: [],
  task_date: "",
  // Data-Analyst-only Schools (schools.id strings, optional).
  school_ids: [],
  tat: { level: "", value: "", unit: "" },
};

// Sentinel <option> value for the Data-Analyst-only "+ Add New ..." entries.
// Never a real id, so it's never written into the form.
const ADD_NEW = "__add_new__";

const toOptions = (rows) => rows.map((r) => ({ value: String(r.id), label: r.name }));

const EMPTY_SUBCATEGORY_FIELDS = { description: "" };

const QUICK_ADD_COPY = {
  category: { title: "Add New Category", label: "Category name", endpoint: "/categories/own" },
  subcategory: { title: "Add New Subcategory", label: "Subcategory name", endpoint: "/subcategories/own" },
  school: { title: "Add New School", label: "School name", endpoint: "/schools/own" },
};
// Recent TAT Entries: rows shown collapsed / after "View history".
const RECENT_TAT_COLLAPSED = 3;
const RECENT_TAT_EXPANDED = 20;

const QUICK_ADD_MAX_LENGTH = { category: 100, subcategory: 255, school: 150 };

export default function OwnTicketModal({ show, onClose, onCreated }) {
  const { user } = useContext(AuthContext);
  // One Data Analyst check for every DA-only feature below. The login payload
  // carries `role` ("data-analyst") but GET /auth/me only returns `role_name`
  // ("Data Analyst"), so accept either shape and spelling.
  const rawRole = user?.role ?? user?.role_name ?? user?.roleName ?? "";
  const normalizedRole = String(rawRole).trim().toLowerCase().replace(/[\s_]+/g, "-");
  const isDataAnalyst = normalizedRole === "data-analyst";
  // Quick-add is Data Analyst only (Developers share this modal but don't get
  // it); the backend (POST /categories/own, /subcategories/own) enforces the
  // same role independently.
  const canQuickAdd = isDataAnalyst;
  // Optional School -- Data Analyst only as well, from the schools master
  // (GET /schools) with "+ Add New School" (POST /schools/own). The backend
  // rejects a school -- and school creation -- from anyone else.
  const canSetSchool = isDataAnalyst;
  // Manual per-ticket TAT replaces the subcategory's Estimated TAT.
  const canSetManualTat = isDataAnalyst;
  // An Own Ticket (Data Analyst or Developer) is always in the caller's own
  // unit -- shown locked, never chosen. The backend ignores any client
  // unit_id for this flow and uses users.unit_id, so it isn't sent either.
  const ownUnitId = user?.unit_id ? String(user.unit_id) : "";
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [quickAdd, setQuickAdd] = useState(null); // null | "category" | "subcategory"
  const [quickAddName, setQuickAddName] = useState("");
  // Subcategory-only configurable fields (same set as Admin Subcategory
  // Management's form, minus Unit/Staff: the Unit is already chosen in this
  // modal and the Staff mapping is always the creator, set by the backend).
  const [quickAddSub, setQuickAddSub] = useState(EMPTY_SUBCATEGORY_FIELDS);
  const [quickAddError, setQuickAddError] = useState("");
  const [quickAddSaving, setQuickAddSaving] = useState(false);

  // GET /units is only used to show the own unit's name.
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [form, setForm] = useState({ ...initialForm, task_date: todayDateString() });
  const [tatHistory, setTatHistory] = useState([]);
  const [loadingTatHistory, setLoadingTatHistory] = useState(false);
  // History is informational only -- a failed fetch never hides or blocks
  // the manual TAT fields.
  const [tatHistoryError, setTatHistoryError] = useState(false);
  const [tatTouched, setTatTouched] = useState(false);
  // Recent TAT Entries -> "Edit": the task whose TAT is being edited.
  const [tatEditTaskId, setTatEditTaskId] = useState(null);
  // Recent TAT Entries starts compact (latest few rows) so it never pushes
  // the ticket fields / TAT controls below the fold.
  const [tatHistoryExpanded, setTatHistoryExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    // A failed fetch only costs the label ("Your unit"); it never blocks.
    api.get("/units")
      .then((res) => { if (!cancelled) setUnits(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => { if (!cancelled) { console.error("Fetch units error:", err); setUnits([]); } });
    return () => { cancelled = true; };
  }, [show]);

  const loadSchools = async () => {
    try {
      setLoadingSchools(true);
      const res = await api.get("/schools");
      setSchools(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch schools error:", err);
      setSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  };

  useEffect(() => {
    if (show && canSetSchool) loadSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, canSetSchool]);

  const loadTatHistory = async (isCancelled = () => false) => {
    try {
      setLoadingTatHistory(true);
      setTatHistoryError(false);
      const res = await api.get("/tasks/data-analyst-tat-history");
      if (!isCancelled()) setTatHistory(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (!isCancelled()) {
        console.error("Fetch Data Analyst TAT history error:", err);
        setTatHistory([]);
        setTatHistoryError(true);
      }
    } finally {
      if (!isCancelled()) setLoadingTatHistory(false);
    }
  };

  useEffect(() => {
    if (!show || !canSetManualTat) return;
    let cancelled = false;
    loadTatHistory(() => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, canSetManualTat]);

  // Reset to a clean form every time the modal is (re)opened -- mirrors
  // TaskCreateModal/TaskCreateForm's own "fresh mount" behavior instead of
  // carrying stale values from a previous Own Ticket into a new one.
  useEffect(() => {
    if (show) {
      setForm({ ...initialForm, task_date: todayDateString() });
      setTatTouched(false);
      setTatEditTaskId(null);
      setTatHistoryExpanded(false);
      if (ownUnitId) handleUnitChange(ownUnitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleTatChange = (tat) => {
    handleChange("tat", tat);
    setTatTouched(true);
  };

  // Append (never duplicate) an id to a Data Analyst multi-select.
  const addToSelection = (field, id) =>
    setForm((prev) => (prev[field].includes(String(id)) ? prev : { ...prev, [field]: [...prev[field], String(id)] }));

  // Category ownership only, never unit-scoped -- unit_id is kept purely as
  // the existing UX gate (pick a unit before Category unlocks), matching
  // TaskManagementPage.js's own Add Task flow exactly; the endpoint itself
  // ignores unit_id and resolves ownership from req.user.id alone.
  const loadCategories = () =>
    api.get("/categories/technician-owned")
      .then((res) => setCategories(Array.isArray(res.data) ? res.data : []))
      .catch((err) => { console.error("Fetch owned categories error:", err); setCategories([]); });

  const loadSubCategories = async (categoryId) => {
    try {
      setLoadingSubCategories(true);
      const res = await api.get(`/subcategories/technician-owned/${categoryId}`);
      setSubCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch owned subcategories error:", err);
      setSubCategories([]);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const handleUnitChange = (unitId) => {
    handleChange("unit_id", unitId);
    handleChange("category_id", "");
    handleChange("sub_category_id", "");
    handleChange("sub_category_ids", []);
    setSubCategories([]);
    if (!unitId) {
      setCategories([]);
      return;
    }
    loadCategories();
  };

  const handleCategoryChange = async (categoryId) => {
    if (categoryId === ADD_NEW) return openQuickAdd("category");
    handleChange("category_id", categoryId);
    // Every subcategory belongs to exactly one Category, so a Category change
    // makes all previously selected subcategories incompatible.
    handleChange("sub_category_id", "");
    handleChange("sub_category_ids", []);
    setSubCategories([]);
    if (!categoryId) return;
    await loadSubCategories(categoryId);
  };

  const handleSubCategoryChange = (subCategoryId) => {
    if (subCategoryId === ADD_NEW) return openQuickAdd("subcategory");
    handleChange("sub_category_id", subCategoryId);
  };

  const openQuickAdd = (kind) => {
    setQuickAddName("");
    setQuickAddError("");
    setQuickAddSub(EMPTY_SUBCATEGORY_FIELDS);
    setQuickAdd(kind);
  };

  const closeQuickAdd = () => {
    if (quickAddSaving) return;
    setQuickAdd(null);
  };

  // Saves through the Data-Analyst-scoped endpoints, then refreshes just the
  // affected dropdown and selects the new entry -- no page reload. The
  // client-side duplicate check is only instant feedback; the backend
  // re-checks (case-insensitive, per Unit / per Category) and returns 409.
  // "+ Add New School": creates the School in the schools master, reloads the
  // dropdown and selects the new id -- the ticket then sends only that id.
  // A name that already exists (the backend compares case-insensitively
  // after trimming, 409 SCHOOL_EXISTS) selects the existing School instead.
  const saveNewSchool = async (name) => {
    const selectSchool = async (id, message) => {
      await loadSchools();
      addToSelection("school_ids", id);
      toast.success(message);
      setQuickAdd(null);
    };
    const existing = schools.find((s) => String(s.name).trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      setQuickAddError(`"${existing.name}" already exists — select it from the list instead.`);
      return;
    }
    try {
      setQuickAddSaving(true);
      setQuickAddError("");
      const res = await api.post(QUICK_ADD_COPY.school.endpoint, { name });
      await selectSchool(res.data?.id, "School added");
    } catch (err) {
      const dup = err.response?.status === 409 && err.response?.data?.details?.school;
      if (dup) {
        await selectSchool(dup.id, `"${dup.name}" already exists — selected it`);
        return;
      }
      console.error("Add school error:", err);
      setQuickAddError(err.response?.data?.message || err.response?.data?.error || "Could not save. Please try again.");
    } finally {
      setQuickAddSaving(false);
    }
  };

  const saveQuickAdd = async () => {
    const name = quickAddName.trim().replace(/\s+/g, " ");
    if (!name) {
      setQuickAddError("Please enter a name.");
      return;
    }
    if (quickAdd === "school") return saveNewSchool(name);
    const isCategory = quickAdd === "category";
    if (!isCategory && !form.category_id) {
      setQuickAddError("Please select a Category before adding a subcategory.");
      return;
    }
    const existing = isCategory ? categories : subCategories;
    if (existing.some((item) => String(item.name).trim().toLowerCase() === name.toLowerCase())) {
      setQuickAddError(`"${name}" already exists — select it from the list instead.`);
      return;
    }
    try {
      setQuickAddSaving(true);
      setQuickAddError("");
      // Only the fields the Data-Analyst endpoint accepts are sent; owner,
      // staff mapping and ids are always set by the backend.
      const payload = isCategory
        ? { name, unit_id: Number(form.unit_id) }
        : {
            name,
            category_id: Number(form.category_id),
            description: quickAddSub.description.trim() || null,
            tat_value: null,
            tat_unit: null,
          };
      const res = await api.post(QUICK_ADD_COPY[quickAdd].endpoint, payload);
      const newId = String(res.data?.id);

      if (isCategory) {
        await loadCategories();
        handleChange("category_id", newId);
        handleChange("sub_category_id", "");
        handleChange("sub_category_ids", []);
        setSubCategories([]);
        await loadSubCategories(newId);
        toast.success("Category added");
      } else {
        await loadSubCategories(form.category_id);
        // Quick-add is Data Analyst only, whose subcategories are a list.
        addToSelection("sub_category_ids", newId);
        toast.success("Subcategory added");
      }
      setQuickAdd(null);
    } catch (err) {
      console.error("Quick-add error:", err);
      setQuickAddError(err.response?.data?.message || err.response?.data?.error || "Could not save. Please try again.");
    } finally {
      setQuickAddSaving(false);
    }
  };

  // Data Analyst subcategories: only the loaded Category's own list is ever
  // offered, and anything not in it is dropped from the selection.
  useEffect(() => {
    if (!isDataAnalyst || loadingSubCategories) return;
    setForm((prev) => {
      const valid = new Set(subCategories.map((sc) => String(sc.id)));
      const kept = prev.sub_category_ids.filter((id) => valid.has(id));
      return kept.length === prev.sub_category_ids.length ? prev : { ...prev, sub_category_ids: kept };
    });
  }, [subCategories, loadingSubCategories, isDataAnalyst]);

  const selectedCategoryName = categories.find((c) => String(c.id) === String(form.category_id))?.name;
  const selectedSubCategory = subCategories.find((sc) => String(sc.id) === String(form.sub_category_id));
  const subCategoryHasTat = !!(selectedSubCategory && selectedSubCategory.tat_value != null && selectedSubCategory.tat_unit);

  // Same rules the backend enforces (services/dataAnalystTatService.js).
  const manualTatComplete = !canSetManualTat || Object.values(tatErrors(form.tat)).every((e) => !e);

  // Data Analyst: one or more subcategories (required); Schools are optional
  // (zero, one or many). Developer: one subcategory, no school.
  const subCategoryChosen = isDataAnalyst ? form.sub_category_ids.length > 0 : !!form.sub_category_id;

  const otherFieldsComplete =
    !!form.title.trim() &&
    !!form.unit_id &&
    !!form.category_id &&
    subCategoryChosen &&
    !!form.task_date;

  const canSubmit = otherFieldsComplete && manualTatComplete;

  // TAT errors show once the picker was used, or once everything else is
  // filled in (so a disabled Create is explained).
  const showTatErrors = tatTouched || otherFieldsComplete;

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  // Only schools.id values are ever sent (never a name).
  const schoolPayload = () =>
    canSetSchool ? { school_ids: form.school_ids.map(Number) } : {};

  // Data Analyst sends the whole list plus the first as the primary
  // sub_category_id (what every older reader of tasks.sub_category_id sees).
  const subCategoryPayload = () =>
    isDataAnalyst
      ? { sub_category_id: Number(form.sub_category_ids[0]), sub_category_ids: form.sub_category_ids.map(Number) }
      : { sub_category_id: Number(form.sub_category_id) };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSaving(true);
      const res = await api.post("/tasks", {
        title: form.title.trim(),
        description: form.description.trim(),
        // Own Ticket is always self-owned -- requested_for/assigned_to are
        // both the logged-in user, never client-editable, so there is
        // nothing here for a crafted request to spoof; the backend
        // independently forces created_by to the authenticated actor
        // regardless (PHASE_14_TASK_CREATOR_FIX, controllers/
        // taskController.js#createTask) and re-validates Category/
        // Subcategory ownership on this same self-assign path
        // (services/categoryStaffService.js#isOwnedByUser).
        requested_for: user?.id,
        assigned_to: user?.id,
        category_id: Number(form.category_id),
        ...subCategoryPayload(),
        task_date: form.task_date,
        // High/Medium/Low: only the level (server derives 4h / 2d / 5d);
        // New Requirement: level + custom value + unit.
        ...(canSetManualTat ? tatPayload(form.tat) : {}),
        ...schoolPayload(),
      });
      toast.success("Ticket created successfully");
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      console.error("Create Own Ticket error:", err);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to create ticket");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered scrollable className="unit-modal ticket-modal-theme own-ticket-modal">
      <Modal.Header closeButton>
        <Modal.Title>Own Ticket</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row g-3">
          {/* Redundant for a Data Analyst (always their own ticket), so hidden
              -- display only; the payload still sends the logged-in user. */}
          {!isDataAnalyst && (
            <div className="col-12">
              <Form.Group>
                <Form.Label>Staff / User</Form.Label>
                <Form.Control readOnly disabled value={user?.name || "You"} />
                <Form.Text className="text-muted">This ticket is created for and owned by you.</Form.Text>
              </Form.Group>
            </div>
          )}

          <div className="col-12">
            <Form.Group>
              <Form.Label>Title <span className="text-danger">*</span></Form.Label>
              <Form.Control
                placeholder="Enter task title"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="col-12">
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter task description"
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="col-md-6">
            <Form.Group controlId="own-ticket-unit">
              <Form.Label>Unit <span className="text-danger">*</span></Form.Label>
              <Form.Select value={ownUnitId} disabled>
                {ownUnitId ? (
                  <option value={ownUnitId}>
                    {units.find((unit) => String(unit.id) === ownUnitId)?.name || "Your unit"}
                  </option>
                ) : (
                  <option value="">No unit assigned</option>
                )}
              </Form.Select>
              {!ownUnitId && (
                <Form.Text className="text-danger">
                  No Unit is assigned to your account. Please contact an administrator.
                </Form.Text>
              )}
            </Form.Group>
          </div>

          {/* Simplified backdated Task Date -- same rule as every other
              creation form: defaults to today, `max` blocks a future pick
              in the UI as a courtesy only, the backend independently
              re-validates and rejects a future date regardless. */}
          <div className="col-md-6">
            <Form.Label htmlFor="own-ticket-task-date">Task Date <span className="text-danger">*</span></Form.Label>
            <Form.Control
              id="own-ticket-task-date"
              type="date"
              max={todayDateString()}
              value={form.task_date}
              onChange={(e) => handleChange("task_date", e.target.value)}
            />
          </div>
          {canSetSchool && (
            <div className="col-md-6">
              <Form.Group>
                <Form.Label htmlFor="own-ticket-schools">Schools</Form.Label>
                <MultiSelectChips
                  inputId="own-ticket-schools"
                  options={toOptions(schools)}
                  value={form.school_ids}
                  onChange={(ids) => handleChange("school_ids", ids)}
                  placeholder="Search schools..."
                  loading={loadingSchools}
                  noOptionsText="No matching school"
                />
                <Button variant="link" size="sm" className="px-0" onClick={() => openQuickAdd("school")}>
                  + Add New School
                </Button>
              </Form.Group>
            </div>
          )}

          <div className="col-md-6">
            <Form.Group controlId="own-ticket-category">
              <Form.Label>Category <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={form.category_id}
                disabled={!form.unit_id}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
                {canQuickAdd && <option value={ADD_NEW}>+ Add New Category</option>}
              </Form.Select>
              {form.unit_id && categories.length === 0 && (
                <Form.Text className="text-muted">
                  {canQuickAdd ? "No Category is assigned to you yet — use “+ Add New Category”." : "No Category is assigned to you yet."}
                </Form.Text>
              )}
            </Form.Group>
          </div>

          {isDataAnalyst ? (
            <div className="col-12">
              <Form.Group>
                <Form.Label htmlFor="own-ticket-subcategories">Subcategories <span className="text-danger">*</span></Form.Label>
                <MultiSelectChips
                  inputId="own-ticket-subcategories"
                  options={toOptions(subCategories)}
                  value={form.sub_category_ids}
                  onChange={(ids) => handleChange("sub_category_ids", ids)}
                  placeholder={form.category_id ? "Search subcategories..." : "Select a Category first"}
                  disabled={!form.category_id}
                  loading={loadingSubCategories}
                  noOptionsText="No more subcategories in this category"
                />
                {canQuickAdd && form.category_id && (
                  <Button variant="link" size="sm" className="px-0" onClick={() => openQuickAdd("subcategory")}>
                    + Add New Subcategory
                  </Button>
                )}
                {canQuickAdd && !form.category_id && (
                  <Form.Text className="text-muted d-block">Select a Category first to choose or add subcategories.</Form.Text>
                )}
              </Form.Group>
            </div>
          ) : (
            <div className="col-12">
              <Form.Group controlId="own-ticket-subcategory">
                <Form.Label>Subcategory <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={form.sub_category_id}
                  disabled={!form.category_id || loadingSubCategories}
                  onChange={(e) => handleSubCategoryChange(e.target.value)}
                >
                  <option value="">{loadingSubCategories ? "Loading..." : "Select Subcategory"}</option>
                  {subCategories.map((sc) => (
                    <option key={sc.id} value={sc.id}>{sc.name}</option>
                  ))}
                  {canQuickAdd && form.category_id && <option value={ADD_NEW}>+ Add New Subcategory</option>}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {canSetManualTat && (
            <div className="col-12">
              <DataAnalystTatPicker
                idPrefix="own-ticket-tat"
                value={form.tat}
                onChange={handleTatChange}
                showErrors={showTatErrors}
              />
            </div>
          )}

          {form.sub_category_id && !canSetManualTat && (
            <div className="col-12">
              <Form.Group>
                <Form.Label>Estimated TAT</Form.Label>
                {subCategoryHasTat ? (
                  <Form.Control readOnly value={String(selectedSubCategory.tat_value) + " " + selectedSubCategory.tat_unit} />
                ) : (
                  <Form.Text className="text-muted d-block">
                    No Turnaround Time is configured for this subcategory yet — it will be created without a due date.
                  </Form.Text>
                )}
              </Form.Group>
            </div>
          )}

          {canSetManualTat && (
            <div className="col-12">
              <section className="own-ticket-tat-recent" aria-labelledby="own-ticket-tat-recent-title">
                <div className="own-ticket-tat-recent-head">
                  <div id="own-ticket-tat-recent-title" className="own-ticket-tat-recent-title">Recent TAT Entries</div>
                  {tatHistory.length > RECENT_TAT_COLLAPSED && (
                    <Button
                      variant="link"
                      size="sm"
                      className="own-ticket-tat-recent-toggle"
                      aria-expanded={tatHistoryExpanded}
                      onClick={() => setTatHistoryExpanded((v) => !v)}
                    >
                      {tatHistoryExpanded ? "Show less" : `View history (${tatHistory.length})`}
                    </Button>
                  )}
                </div>
                {loadingTatHistory ? (
                  <div className="own-ticket-tat-recent-empty">Loading TAT entries...</div>
                ) : tatHistoryError ? (
                  <div className="own-ticket-tat-recent-empty">Couldn't load recent TAT entries. You can still create this ticket.</div>
                ) : tatHistory.length === 0 ? (
                  <div className="own-ticket-tat-recent-empty">No manual TAT entry has been created yet.</div>
                ) : (
                  <div className="own-ticket-tat-table-wrap">
                    <table className="own-ticket-tat-table">
                      <thead>
                        <tr>
                          <th scope="col">User ID</th>
                          <th scope="col">Added By</th>
                          <th scope="col">TAT</th>
                          <th scope="col">Level</th>
                          <th scope="col">Task</th>
                          <th scope="col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tatHistory.slice(0, tatHistoryExpanded ? RECENT_TAT_EXPANDED : RECENT_TAT_COLLAPSED).map((row) => (
                          <tr key={row.id}>
                            <td>{row.user_id}</td>
                            <td>{row.user_name || "—"}</td>
                            {/* Stored values are shown as-is: an older entry
                                (e.g. Medium / 20 Days) stays a historical fact. */}
                            <td className="own-ticket-tat-nowrap">{formatTat(row.tat_value, row.tat_unit)}</td>
                            <td>
                              <span className={`own-ticket-tat-level own-ticket-tat-level-${String(row.tat_level || "").toLowerCase()}`}>
                                {levelLabel(row.tat_level)}
                              </span>
                            </td>
                            <td className="own-ticket-tat-task" title={row.task_title || undefined}>
                              <span className="own-ticket-tat-task-id">#{row.task_id}</span> {row.task_title || ""}
                            </td>
                            <td>
                              {row.can_edit ? (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  aria-label={`Edit TAT of ticket #${row.task_id}`}
                                  onClick={() => setTatEditTaskId(row.task_id)}
                                >
                                  Edit
                                </Button>
                              ) : (
                                <span className="text-muted" title="Completed or cancelled tickets cannot be edited">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}

        </div>

        <small className="text-muted d-block mt-3">Fields marked with * are required.</small>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button className="unit-btn unit-btn-primary" onClick={handleSubmit} disabled={saving || !canSubmit}>
          {saving ? "Creating..." : "Create"}
        </Button>
      </Modal.Footer>

      {canQuickAdd && (
        <Modal
          show={!!quickAdd}
          onHide={closeQuickAdd}
          centered
          size={quickAdd === "subcategory" ? undefined : "sm"}
          className="unit-modal ticket-modal-theme own-ticket-quickadd"
          backdropClassName="own-ticket-quickadd-backdrop"
          aria-labelledby="own-ticket-quickadd-title"
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              saveQuickAdd();
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title id="own-ticket-quickadd-title">{quickAdd ? QUICK_ADD_COPY[quickAdd].title : ""}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {quickAdd === "subcategory" && (
                <Form.Group className="mb-3" controlId="own-ticket-quickadd-category">
                  <Form.Label>Category</Form.Label>
                  {/* Parent Category is fixed to the one chosen in Own Ticket
                      -- shown for context, never editable here. */}
                  <Form.Control readOnly disabled value={selectedCategoryName || ""} />
                </Form.Group>
              )}

              <Form.Group controlId="own-ticket-quickadd-name">
                <Form.Label>{quickAdd ? QUICK_ADD_COPY[quickAdd].label : ""} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  autoFocus
                  maxLength={QUICK_ADD_MAX_LENGTH[quickAdd] || 255}
                  value={quickAddName}
                  isInvalid={!!quickAddError}
                  onChange={(e) => {
                    setQuickAddName(e.target.value);
                    if (quickAddError) setQuickAddError("");
                  }}
                />
                <Form.Control.Feedback type="invalid">{quickAddError}</Form.Control.Feedback>
              </Form.Group>

              {quickAdd === "subcategory" && (
                <>
                  <Form.Group className="mt-3" controlId="own-ticket-quickadd-description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter description"
                      value={quickAddSub.description}
                      onChange={(e) => setQuickAddSub((prev) => ({ ...prev, description: e.target.value }))}
                    />
                  </Form.Group>

                  <Form.Text className="text-muted d-block mt-3">
                    TAT is selected per Own Ticket by the Data Analyst, not stored on this subcategory.
                  </Form.Text>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeQuickAdd} disabled={quickAddSaving}>
                Cancel
              </Button>
              <Button type="submit" className="unit-btn unit-btn-primary" disabled={quickAddSaving || !quickAddName.trim()}>
                {quickAddSaving ? "Saving..." : "Save"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {canSetManualTat && (
        <EditOwnTicketModal
          show={!!tatEditTaskId}
          taskId={tatEditTaskId}
          mode="tat"
          onClose={() => setTatEditTaskId(null)}
          onSaved={() => loadTatHistory()}
        />
      )}
    </Modal>
  );
}
